import { publicAxios } from "../../../../config/axios";
import constants from "../../../../utils/constants";
import type { ApiCallRecord, ApiRequestSpec, ApiResult, StepActor } from "../types";
import { ApiCallError, extractApiErrorMessage } from "./errors";

let callCounter = 0;
const nextCallId = () => `call_${Date.now().toString(36)}_${++callCounter}`;

export const getOrcaToken = (): string =>
  localStorage.getItem(constants.AUTH_TOKEN) || "";

export const getBaseUrl = (): string =>
  (publicAxios?.defaults?.baseURL as string) ||
  process.env.REACT_APP_BASE_URL ||
  "";

const joinUrl = (base: string, path: string) => {
  if (/^https?:\/\//i.test(path)) return path;
  const trimmedBase = base.replace(/\/+$/, "");
  const trimmedPath = path.startsWith("/") ? path : `/${path}`;
  return `${trimmedBase}${trimmedPath}`;
};

const redactToken = (value: string) => {
  if (!value) return value;
  if (value.length <= 12) return "<token>";
  return `${value.slice(0, 8)}…<redacted>`;
};

const shellQuote = (value: string) => `'${value.replace(/'/g, `'\\''`)}'`;

const redactHeaders = (headers: Record<string, string>) =>
  Object.fromEntries(
    Object.entries(headers).map(([k, v]) => [
      k,
      k.toLowerCase() === "authorization" ? redactToken(v) : v,
    ])
  );

export const buildCurl = (
  method: string,
  url: string,
  headers: Record<string, string>,
  body?: unknown,
  isMultipart?: boolean
): string => {
  const parts = [`curl --location --request ${method} ${shellQuote(url)}`];
  Object.entries(headers).forEach(([k, v]) => {
    const printable =
      k.toLowerCase() === "authorization" ? redactToken(v) : v;
    parts.push(`  --header ${shellQuote(`${k}: ${printable}`)}`);
  });
  if (isMultipart) {
    parts.push(`  --form 'media=@"/path/to/file.pdf"'`);
  } else if (body !== undefined && body !== null) {
    const json = typeof body === "string" ? body : JSON.stringify(body);
    parts.push(`  --data-raw ${shellQuote(json)}`);
  }
  return parts.join(" \\\n");
};

const describeFormData = (fd: FormData) => {
  const out: Record<string, string> = {};
  fd.forEach((value, key) => {
    if (value instanceof File) {
      out[key] = `<file ${value.name} ${value.type} ${value.size}B>`;
    } else {
      out[key] = String(value);
    }
  });
  return out;
};

const redactQuery = (url: string, keys?: string[]) => {
  if (!keys?.length) return url;
  return keys.reduce(
    (acc, key) =>
      acc.replace(new RegExp(`([?&]${key}=)[^&#]*`, "gi"), "$1<redacted>"),
    url
  );
};

const redactResponse = (body: unknown, keys?: string[]) => {
  if (!keys?.length || !body || typeof body !== "object" || Array.isArray(body)) {
    return body;
  }
  const clone: Record<string, unknown> = { ...(body as Record<string, unknown>) };
  keys.forEach((k) => {
    if (k in clone && clone[k]) clone[k] = "<redacted>";
  });
  return clone;
};

export interface RecordingApiOptions {
  stepId: string;
  onRecord: (record: ApiCallRecord) => void;
  signal?: AbortSignal;
}

/**
 * Executes an HTTP request through the shared OrCA axios instance while
 * capturing method, URL, headers, payload, status, response, timing and a
 * copyable cURL for the timeline.
 */
export const executeRecorded = async <T = any>(
  spec: ApiRequestSpec,
  opts: RecordingApiOptions
): Promise<ApiResult<T>> => {
  const baseUrl = getBaseUrl();
  const url = joinUrl(baseUrl, spec.path);
  const actor: StepActor = spec.actor || "ORCA";
  const token = spec.authToken ?? getOrcaToken();
  const headers: Record<string, string> = {
    Accept: "*/*",
    ...(spec.formData ? {} : { "Content-Type": "application/json" }),
    ...(token ? { Authorization: token } : {}),
    ...(spec.extraHeaders || {}),
  };

  const displayUrl = redactQuery(url, spec.redactQueryKeys);
  const record: ApiCallRecord = {
    id: nextCallId(),
    stepId: opts.stepId,
    label: spec.label,
    method: spec.method,
    url: displayUrl,
    path: redactQuery(spec.path, spec.redactQueryKeys),
    headers: redactHeaders(headers),
    requestBody: spec.formData ? describeFormData(spec.formData) : spec.body,
    startedAt: Date.now(),
    curl: buildCurl(spec.method, displayUrl, headers, spec.body, Boolean(spec.formData)),
    isPoll: spec.isPoll,
    actor,
  };

  try {
    const response = await publicAxios.request<T>({
      url: spec.path,
      method: spec.method,
      headers,
      data: spec.formData ?? spec.body,
      signal: opts.signal,
    });
    record.status = response.status;
    record.responseBody = redactResponse(response.data, spec.redactResponseKeys);
    record.endedAt = Date.now();
    record.durationMs = record.endedAt - record.startedAt;
    opts.onRecord(record);
    return { data: response.data, status: response.status, record };
  } catch (err: any) {
    record.endedAt = Date.now();
    record.durationMs = record.endedAt - record.startedAt;
    record.status = err?.response?.status;
    record.responseBody = err?.response?.data;
    record.errorMessage = extractApiErrorMessage(err);
    opts.onRecord(record);
    if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
      throw err;
    }
    throw new ApiCallError(
      `${spec.label} failed${record.status ? ` (HTTP ${record.status})` : ""}: ${record.errorMessage}`,
      record
    );
  }
};
